import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Users } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/Select';
import { apiFetch } from '../lib/api';
import { cn } from '../lib/utils';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatPercent,
} from '../lib/format';
import type {
  BuyerCouponSummary,
  BuyerGapKey,
  BuyerPeriod,
  BuyerSegmentId,
  BuyerType,
  BuyersGroups,
  BuyersOverview,
  BuyersResult,
  BuyersWhy,
} from '../types/api';

type Tab = 'overview' | 'why' | 'groups';
const TABS: { value: Tab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'why', label: 'Why they bought' },
  { value: 'groups', label: 'Customer groups' },
];

const PERIODS: { value: string; label: string }[] = [
  { value: '365', label: 'Last 12 months' },
  { value: '90', label: 'Last 90 days' },
  { value: '30', label: 'Last 30 days' },
];

const TYPE_INFO: Record<BuyerType, { label: string; hint: string; swatch: string }> = {
  new: { label: 'New customer', hint: 'First paid order ever', swatch: 'bg-[#c7c8f5]' },
  regular: { label: 'Regular', hint: 'Bought 0-90 days before', swatch: 'bg-accent' },
  back: { label: 'Came back', hint: 'Bought 91-365 days before', swatch: 'bg-[#2f8f6b]' },
  won: { label: 'Won back', hint: 'Nothing for over a year', swatch: 'bg-[#d97706]' },
};
const TYPE_ORDER: BuyerType[] = ['new', 'regular', 'back', 'won'];

const GAP_LABELS: Record<BuyerGapKey, string> = {
  d30: '0-30 days',
  d90: '31-90 days',
  d180: '91-180 days',
  d365: '181-365 days',
  y2: '1-2 years',
  y2plus: 'Over 2 years',
};

const SEGMENTS: Record<BuyerSegmentId, { label: string; rule: string; action: string }> = {
  loyal: {
    label: 'Loyal',
    rule: '3 or more orders, the last within a year.',
    action: 'Thank them, ask for a review and let them try new products first.',
  },
  repeat: {
    label: 'Repeat',
    rule: '2 orders, the last within a year.',
    action: 'Suggest a refill before the cream runs out. A third order makes them loyal.',
  },
  new: {
    label: 'New',
    rule: 'First order in the last 90 days, one order only.',
    action: 'Welcome them, ask how the healing goes and offer a refill after about two months.',
  },
  one: {
    label: 'One-time',
    rule: 'One order, 91-365 days ago.',
    action: 'Remind them before a year has passed: a refill or a new product.',
  },
  lapsedrep: {
    label: 'Lapsed regulars',
    rule: '2 or more orders, none for over a year.',
    action: 'Write to them personally and ask what changed. They knew the product.',
  },
  lapsed: {
    label: 'Lapsed one-time',
    rule: 'One order, more than a year ago.',
    action: 'A light win-back email. Leave out those who never open.',
  },
};
const SEGMENT_ORDER: BuyerSegmentId[] = ['loyal', 'repeat', 'new', 'one', 'lapsedrep', 'lapsed'];

function tabFromParam(raw: string | null): Tab {
  return TABS.some((t) => t.value === raw) ? (raw as Tab) : 'overview';
}

function updatedText(computedAt: number | null, stale: boolean): string | null {
  if (computedAt === null) return null;
  return `Updated ${formatDateTime(computedAt)}${stale ? ', a newer result is being calculated' : ''}. Results are refreshed once an hour.`;
}

function countryName(code: string | null): string {
  if (code === null) return 'Unknown';
  try {
    return new Intl.DisplayNames('en', { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleString('en-GB', {
    month: 'short',
    timeZone: 'UTC',
  });
}

// ---- small parts ----------------------------------------------------------

function Section({
  title,
  sub,
  children,
  className,
}: {
  title: string;
  sub?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn('p-4 grid gap-3 min-w-0 content-start', className)}>
      <div>
        <h2 className="m-0 text-[15px] font-semibold">{title}</h2>
        {sub ? <p className="m-0 mt-0.5 text-xs text-muted max-w-[70ch]">{sub}</p> : null}
      </div>
      {children}
    </Card>
  );
}

function BarList({
  rows,
  total,
  empty = 'Nothing in this period.',
}: {
  rows: { label: string; value: number }[];
  /** Share base; defaults to the largest row. */
  total?: number;
  empty?: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  const base = total && total > 0 ? total : null;
  if (rows.every((r) => r.value === 0)) return <p className="m-0 text-sm text-muted">{empty}</p>;
  return (
    <ul className="m-0 p-0 list-none grid gap-1.5">
      {rows.map((r) => (
        <li
          key={r.label}
          className="grid grid-cols-[minmax(80px,140px)_minmax(0,1fr)_84px] gap-2.5 items-center text-[13px]"
        >
          <span className="text-muted truncate" title={r.label}>
            {r.label}
          </span>
          <span className="h-2.5 rounded-[3px] bg-sunken overflow-hidden" aria-hidden="true">
            <span
              className="block h-full rounded-[3px] bg-accent"
              style={{ width: `${(r.value / max) * 100}%` }}
            />
          </span>
          <span className="text-right tabular-nums">
            {formatNumber(r.value)}
            {base ? <span className="text-faint"> {formatPercent(r.value / base)}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Kpi({ value, label, note }: { value: string; label: string; note?: string }) {
  return (
    <div className="p-4 grid gap-0.5 content-start min-w-0">
      <b className="text-2xl font-semibold tracking-[-0.01em] tabular-nums">{value}</b>
      <span className="text-xs text-muted">{label}</span>
      {note ? <span className="text-[11px] text-faint">{note}</span> : null}
    </div>
  );
}

function TypePill({ type }: { type: BuyerType }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap">
      <i className={cn('h-2.5 w-2.5 rounded-[2px] inline-block', TYPE_INFO[type].swatch)} />
      {TYPE_INFO[type].label}
    </span>
  );
}

function Pending({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[10px] border border-dashed border-border bg-hover px-3 py-2.5 text-[13px] text-muted">
      {children}
    </div>
  );
}

/** Discount codes. Unknown is never "no code": it says how many orders are known, and waits when none are. */
function CouponBlock({ coupons }: { coupons: BuyerCouponSummary }) {
  if (coupons.totalOrders === 0) return <p className="m-0 text-sm text-muted">No orders.</p>;
  if (coupons.knownOrders === 0) {
    return (
      <Pending>
        Discount codes are not available yet. The shop history is still being fetched, so no order
        has a known discount. Until then this is left empty rather than shown as &quot;no
        code&quot;.
      </Pending>
    );
  }
  const partial = coupons.knownOrders < coupons.totalOrders;
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <span>
          <b className="text-xl font-semibold tabular-nums">
            {formatPercent(coupons.withCode / coupons.knownOrders)}
          </b>{' '}
          <span className="text-sm text-muted">of orders used a code</span>
        </span>
        <span className="text-xs text-muted">
          Known for {formatNumber(coupons.knownOrders)} of {formatNumber(coupons.totalOrders)}{' '}
          orders{partial ? '. The rest are not fetched yet and are not counted as "no code".' : '.'}
        </span>
      </div>
      {coupons.byCode.length > 0 ? (
        <BarList
          rows={coupons.byCode.map((c) => ({ label: c.code, value: c.orders }))}
          total={coupons.knownOrders}
        />
      ) : (
        <p className="m-0 text-sm text-muted">No codes were used on the known orders.</p>
      )}
    </div>
  );
}

function ResultNote({ computedAt, stale }: { computedAt: number | null; stale: boolean }) {
  const text = updatedText(computedAt, stale);
  return text ? <p className="m-0 text-xs text-faint">{text}</p> : null;
}

// ---- Overview -------------------------------------------------------------

function MonthChart({ months }: { months: BuyersOverview['months'] }) {
  const max = Math.max(1, ...months.map((m) => m.first + m.returning));
  return (
    <div className="grid gap-2">
      <div className="flex gap-3 text-xs text-muted flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <i className="h-2.5 w-2.5 rounded-[2px] bg-[#c7c8f5] inline-block" />
          First order
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="h-2.5 w-2.5 rounded-[2px] bg-accent inline-block" />
          Returning
        </span>
      </div>
      <ol
        className="m-0 p-0 list-none flex items-end gap-1 sm:gap-2 h-44"
        aria-label="Paid orders per month, first orders and returning customers"
      >
        {months.map((m) => {
          const total = m.first + m.returning;
          return (
            <li
              key={m.month}
              className="flex-1 min-w-0 h-full flex flex-col justify-end items-stretch gap-1"
              title={`${m.month}${m.partial ? ' (so far)' : ''}: ${formatNumber(m.first)} first, ${formatNumber(m.returning)} returning`}
            >
              <span className="text-[10px] text-faint text-center tabular-nums leading-none">
                {formatNumber(total)}
              </span>
              <span
                className={cn(
                  'flex flex-col justify-end overflow-hidden rounded-[3px]',
                  m.partial && 'opacity-60 outline-dashed outline-1 outline-border',
                )}
                style={{ height: `${(total / max) * 100}%`, minHeight: total > 0 ? 2 : 0 }}
              >
                <span className="bg-accent" style={{ flex: m.returning }} />
                <span className="bg-[#c7c8f5]" style={{ flex: m.first }} />
              </span>
            </li>
          );
        })}
      </ol>
      <ol className="m-0 p-0 list-none flex gap-1 sm:gap-2" aria-hidden="true">
        {months.map((m) => (
          <li
            key={m.month}
            className="flex-1 min-w-0 text-center text-[10px] sm:text-[11px] text-muted leading-tight"
          >
            {monthLabel(m.month)}
            {m.partial ? <span className="block text-[9px] text-faint">so far</span> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function OverviewTab() {
  const [period, setPeriod] = useState<BuyerPeriod>(365);
  const [country, setCountry] = useState('all');
  const query = useQuery({
    queryKey: ['buyers', 'overview', period, country],
    queryFn: () =>
      apiFetch<BuyersResult<BuyersOverview>>(
        `/buyers/overview?period=${period}&country=${country}`,
      ),
  });
  const data = query.data?.data;

  const countryOptions = data?.countryOptions ?? [];
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="flex items-center gap-2">
          <label htmlFor="buyers-period" className="text-[13px] text-muted">
            Period
          </label>
          <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v) as BuyerPeriod)}>
            <SelectTrigger id="buyers-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="buyers-country" className="text-[13px] text-muted">
            Country
          </label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger id="buyers-country">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All countries</SelectItem>
              {countryOptions.map((c) => (
                <SelectItem key={c.country} value={c.country}>
                  {countryName(c.country)}
                </SelectItem>
              ))}
              {country !== 'all' && !countryOptions.some((c) => c.country === country) ? (
                <SelectItem value={country}>{countryName(country)}</SelectItem>
              ) : null}
            </SelectContent>
          </Select>
        </div>
      </div>

      {query.isLoading ? <div className="text-muted">Loading…</div> : null}
      {query.isError ? <Alert>The buyer overview could not be loaded.</Alert> : null}

      {data &&
      data.kpis.paidOrders === 0 &&
      data.months.every((m) => m.first + m.returning === 0) ? (
        <EmptyState
          icon={<Users />}
          title="No paid orders yet"
          description="Paid orders from the shop show up here. Refunded, cancelled and failed orders are not counted."
        />
      ) : null}

      {data && (data.kpis.paidOrders > 0 || data.months.some((m) => m.first + m.returning > 0)) ? (
        <>
          <Card className="grid grid-cols-2 lg:grid-cols-4 divide-border-soft max-lg:[&>*:nth-child(n+3)]:border-t max-lg:[&>*:nth-child(even)]:border-l lg:[&>*+*]:border-l [&>*]:border-border-soft">
            <Kpi value={formatNumber(data.kpis.paidOrders)} label="Paid orders" />
            <Kpi
              value={
                data.kpis.returningShare === null ? '–' : formatPercent(data.kpis.returningShare)
              }
              label="From returning customers"
              note={`${formatNumber(data.kpis.returningOrders)} orders`}
            />
            <Kpi
              value={
                data.kpis.averageOrder === null
                  ? '–'
                  : formatMoney(data.kpis.averageOrder, data.currency, 2)
              }
              label="Average order"
              note="After refunds"
            />
            <Kpi
              value={
                data.kpis.againWithinYearShare === null
                  ? '–'
                  : formatPercent(data.kpis.againWithinYearShare)
              }
              label="Of first-time buyers order again within a year"
              note={`Buyers whose first order was 1-2 years ago (${formatNumber(data.kpis.firstTimers)})`}
            />
          </Card>

          <Section
            title="Orders per month"
            sub="Last 12 months. First order means the buyer's first paid order ever. Not affected by the period."
          >
            <MonthChart months={data.months} />
          </Section>

          <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))]">
            <Section
              title="Days since the previous order"
              sub="Returning orders in the period, by time since that buyer's last paid order."
            >
              <BarList
                rows={data.gap.map((g) => ({ label: GAP_LABELS[g.key], value: g.orders }))}
                total={data.kpis.returningOrders}
                empty="No returning orders in this period."
              />
            </Section>
            <Section title="Orders per customer" sub="All buyers, all time.">
              <BarList
                rows={data.perCustomer.map((p) => ({
                  label: p.key === '1' ? '1 order' : `${p.key} orders`,
                  value: p.buyers,
                }))}
                total={data.perCustomer.reduce((s, p) => s + p.buyers, 0)}
                empty="No buyers yet."
              />
            </Section>
            <Section title="Source" sub="Where the shop says the order came from.">
              <BarList
                rows={[
                  ...data.sources.map((s) => ({
                    label: s.source ?? 'Direct or unknown',
                    value: s.orders,
                  })),
                  ...(data.sourcesOther > 0 ? [{ label: 'Other', value: data.sourcesOther }] : []),
                ]}
                total={data.kpis.paidOrders}
              />
            </Section>
            <Section title="Country">
              <BarList
                rows={[
                  ...data.countries.map((c) => ({
                    label: countryName(c.country),
                    value: c.orders,
                  })),
                  ...(data.countriesOther > 0
                    ? [{ label: 'Other', value: data.countriesOther }]
                    : []),
                ]}
                total={data.kpis.paidOrders}
              />
            </Section>
            <Section title="Top products" sub="Orders that contain the product.">
              <BarList rows={data.products.map((p) => ({ label: p.name, value: p.orders }))} />
            </Section>
            <Section title="Discount codes">
              <CouponBlock coupons={data.coupons} />
            </Section>
          </div>
          <ResultNote
            computedAt={query.data?.computedAt ?? null}
            stale={query.data?.stale ?? false}
          />
        </>
      ) : null}
    </div>
  );
}

// ---- Why they bought ------------------------------------------------------

function WhyTab() {
  const [params, setParams] = useSearchParams();
  const campaign = params.get('campaign') ?? 'all';
  const query = useQuery({
    queryKey: ['buyers', 'why', campaign],
    queryFn: () => apiFetch<BuyersWhy>(`/buyers/why?campaign=${encodeURIComponent(campaign)}`),
  });
  const data = query.data;

  function choose(value: string) {
    const next = new URLSearchParams(params);
    if (value === 'all') next.delete('campaign');
    else next.set('campaign', value);
    setParams(next, { replace: true });
  }

  const options = data?.options ?? [{ value: 'all', label: 'All orders, last 12 months' }];
  return (
    <div className="grid gap-4">
      <div className="flex items-center gap-2 min-w-0">
        <label htmlFor="buyers-campaign" className="text-[13px] text-muted shrink-0">
          Show
        </label>
        <Select value={campaign} onValueChange={choose}>
          <SelectTrigger id="buyers-campaign" className="min-w-0 max-w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
            {!options.some((o) => o.value === campaign) ? (
              <SelectItem value={campaign}>This campaign</SelectItem>
            ) : null}
          </SelectContent>
        </Select>
      </div>

      {query.isLoading ? <div className="text-muted">Loading…</div> : null}
      {query.isError ? <Alert>The buyers could not be loaded for this choice.</Alert> : null}

      {data && data.total === 0 ? (
        <EmptyState
          icon={<Users />}
          title="No paid orders here yet"
          description={
            data.scope.kind === 'all'
              ? 'No paid orders in the last 12 months.'
              : 'No paid order has been matched to this send yet. Orders show up once a buyer has clicked through from it.'
          }
        />
      ) : null}

      {data && data.total > 0 ? (
        <>
          <Section
            title={data.scope.label}
            sub={`${formatNumber(data.total)} paid ${data.total === 1 ? 'order' : 'orders'}. Each is sorted by how long ago the same buyer last ordered.`}
          >
            <div
              className="flex h-7 rounded-md overflow-hidden bg-sunken"
              role="img"
              aria-label={TYPE_ORDER.map(
                (t) => `${TYPE_INFO[t].label} ${formatNumber(data.counts[t])}`,
              ).join(', ')}
            >
              {TYPE_ORDER.map((t) =>
                data.counts[t] > 0 ? (
                  <span
                    key={t}
                    className={TYPE_INFO[t].swatch}
                    style={{ width: `${(data.counts[t] / data.total) * 100}%` }}
                  />
                ) : null,
              )}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
              {TYPE_ORDER.map((t) => (
                <div key={t} className="min-w-0">
                  <TypePill type={t} />
                  <div className="text-2xl font-semibold tabular-nums tracking-[-0.01em]">
                    {formatNumber(data.counts[t])}
                    <span className="ml-1.5 text-xs font-normal text-muted">
                      {formatPercent(data.counts[t] / data.total)}
                    </span>
                  </div>
                  <div className="text-[11px] text-faint">{TYPE_INFO[t].hint}</div>
                </div>
              ))}
            </div>
            <p className="m-0 text-sm" data-testid="why-readout">
              {formatNumber(data.total - data.counts.new)} of {formatNumber(data.total)} had bought
              before
              {data.counts.won > 0
                ? `; ${formatNumber(data.counts.won)} had not bought in over a year`
                : ''}
              .
            </p>
          </Section>

          <Section title="Discount codes" sub="Share of these orders that used a code.">
            <CouponBlock coupons={data.coupons} />
          </Section>

          {data.rows ? (
            <Section
              title="Buyers"
              sub={
                data.rowsTruncated
                  ? 'The 200 newest orders. The figures above cover all of them.'
                  : undefined
              }
            >
              <div className="overflow-x-auto -mx-4">
                <table className="w-full min-w-[640px] text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-xs text-muted uppercase tracking-wide">
                      <th className="px-4 py-2 font-medium">Buyer</th>
                      <th className="px-4 py-2 font-medium">Type</th>
                      <th className="px-4 py-2 font-medium text-right">Orders before</th>
                      <th className="px-4 py-2 font-medium text-right">Days since previous</th>
                      <th className="px-4 py-2 font-medium">Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.orderNumber} className="border-t border-border-soft">
                        <td className="px-4 py-2.5 font-medium break-all">
                          {r.contactId && r.email ? (
                            <Link to={`/kontakter/${r.contactId}`}>{r.email}</Link>
                          ) : (
                            (r.email ?? 'Unknown')
                          )}
                          <div className="text-xs font-normal text-faint">
                            Order {r.orderNumber}, {formatDate(r.orderedAt)}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <TypePill type={r.type} />
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{r.ordersBefore}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {r.daysSincePrevious === null ? '–' : formatNumber(r.daysSincePrevious)}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted">
                          {!r.coupon.known
                            ? 'Not fetched yet'
                            : r.coupon.codes.length > 0
                              ? r.coupon.codes.join(', ')
                              : 'None'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          ) : null}
          {data.excluded > 0 ? (
            <p className="m-0 text-xs text-faint">
              {formatNumber(data.excluded)} {data.excluded === 1 ? 'order' : 'orders'} left out
              because the buyer objected to profiling.
            </p>
          ) : null}
          <ResultNote computedAt={data.computedAt} stale={data.stale} />
        </>
      ) : null}
    </div>
  );
}

// ---- Customer groups ------------------------------------------------------

function cohortShade(share: number | null): string | undefined {
  if (share === null) return undefined;
  // Up to ~35 % of the accent colour; the scale is fixed so quarters compare.
  const strength = Math.min(0.35, share * 1.4);
  return `color-mix(in srgb, var(--color-accent) ${Math.round(strength * 100)}%, transparent)`;
}

function GroupsTab() {
  const [selected, setSelected] = useState<BuyerSegmentId>('loyal');
  const query = useQuery({
    queryKey: ['buyers', 'groups'],
    queryFn: () => apiFetch<BuyersResult<BuyersGroups>>('/buyers/groups'),
  });
  const data = query.data?.data;
  const info = SEGMENTS[selected];
  const count = data?.segments.find((s) => s.id === selected)?.buyers ?? 0;

  return (
    <div className="grid gap-4">
      {query.isLoading ? <div className="text-muted">Loading…</div> : null}
      {query.isError ? <Alert>The customer groups could not be loaded.</Alert> : null}

      {data && data.total === 0 ? (
        <EmptyState
          icon={<Users />}
          title="No buyers yet"
          description="Customer groups appear once the shop has paid orders. Buyers who objected to profiling are not included."
        />
      ) : null}

      {data && data.total > 0 ? (
        <>
          <Section
            title="Groups"
            sub={`${formatNumber(data.total)} buyers, each in exactly one group. Choose a group to see what to do with it.`}
          >
            <div
              role="group"
              aria-label="Customer groups"
              className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(min(100%,150px),1fr))]"
            >
              {SEGMENT_ORDER.map((id) => {
                const n = data.segments.find((s) => s.id === id)?.buyers ?? 0;
                const on = id === selected;
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setSelected(id)}
                    className={cn(
                      'text-left rounded-xl border px-3 py-2.5 grid gap-0.5 transition-colors',
                      on ? 'border-accent bg-accent-soft' : 'border-border bg-card hover:bg-hover',
                    )}
                  >
                    <span className="text-xs text-muted">{SEGMENTS[id].label}</span>
                    <span className="text-xl font-semibold tabular-nums">{formatNumber(n)}</span>
                    <span className="text-[11px] text-faint">{formatPercent(n / data.total)}</span>
                  </button>
                );
              })}
            </div>
            <div className="rounded-xl bg-hover border border-border-soft px-3.5 py-3 grid gap-1">
              <div className="text-sm font-medium">
                {info.label}, {formatNumber(count)} buyers
              </div>
              <div className="text-[13px] text-muted">{info.rule}</div>
              <div className="text-[13px]">
                <span className="text-muted">Suggestion: </span>
                {info.action}
              </div>
            </div>
          </Section>

          <Section
            title="Do first-time buyers order again?"
            sub="First-time buyers per quarter and the share who ordered again within 90, 180 and 365 days. A dash means not enough time has passed yet."
          >
            <div className="overflow-x-auto -mx-4">
              <table className="w-full min-w-[480px] text-sm border-collapse">
                <thead>
                  <tr className="text-left text-xs text-muted uppercase tracking-wide">
                    <th className="px-4 py-2 font-medium">First order in</th>
                    <th className="px-4 py-2 font-medium text-right">Buyers</th>
                    <th className="px-4 py-2 font-medium text-right">90 days</th>
                    <th className="px-4 py-2 font-medium text-right">180 days</th>
                    <th className="px-4 py-2 font-medium text-right">365 days</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cohorts.map((c) => (
                    <tr key={c.quarter} className="border-t border-border-soft">
                      <td className="px-4 py-2 font-medium">{c.quarter.replace('-', ' ')}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatNumber(c.buyers)}
                      </td>
                      {[c.within90, c.within180, c.within365].map((v, i) => (
                        <td
                          key={i}
                          className="px-4 py-2 text-right tabular-nums"
                          style={{ background: cohortShade(v) }}
                        >
                          {v === null ? <span className="text-faint">–</span> : formatPercent(v)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          <ResultNote
            computedAt={query.data?.computedAt ?? null}
            stale={query.data?.stale ?? false}
          />
        </>
      ) : null}
    </div>
  );
}

// ---- Page -----------------------------------------------------------------

/**
 * Results → Buyers. Why do people buy: are they regulars, did they come back
 * after a long time, did they use a code? From the paid orders in the shop
 * (`GET /api/buyers/*`, `buyers.repo.ts`). The heavy figures are cached for an
 * hour on the server.
 */
export function BuyersPage() {
  const [params, setParams] = useSearchParams();
  const tab = tabFromParam(params.get('tab'));

  function choose(next: Tab) {
    const p = new URLSearchParams();
    if (next !== 'overview') p.set('tab', next);
    setParams(p, { replace: true });
  }
  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = TABS[(index + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length];
    if (!next) return;
    choose(next.value);
    (document.getElementById(`buyers-tab-${next.value}`) as HTMLButtonElement | null)?.focus();
  }

  return (
    <div className="grid gap-4 min-w-0">
      <PageHeader
        title="Buyers"
        badge={<MockBadge />}
        description="Why people buy and how often they come back, from paid orders. Refunded and cancelled orders are not counted. Buyers who objected to profiling are left out of the per-person table, the customer groups and the cohorts."
      />
      <div
        role="tablist"
        aria-label="Buyers"
        className="inline-flex gap-1 rounded-lg bg-bg p-1 justify-self-start max-w-full overflow-x-auto"
      >
        {TABS.map((t, i) => (
          <button
            key={t.value}
            id={`buyers-tab-${t.value}`}
            role="tab"
            type="button"
            aria-selected={tab === t.value}
            aria-controls="buyers-panel"
            tabIndex={tab === t.value ? 0 : -1}
            className={cn(
              'rounded-md px-3 h-8 text-sm font-medium transition-colors whitespace-nowrap',
              tab === t.value ? 'bg-card shadow-sm text-text' : 'text-muted hover:text-text',
            )}
            onClick={() => choose(t.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div
        id="buyers-panel"
        role="tabpanel"
        aria-labelledby={`buyers-tab-${tab}`}
        className="min-w-0"
      >
        {tab === 'overview' ? <OverviewTab /> : null}
        {tab === 'why' ? <WhyTab /> : null}
        {tab === 'groups' ? <GroupsTab /> : null}
      </div>
    </div>
  );
}
